import { useQuery, useSubscription } from "@apollo/client";
import {
    getMillLocalserverCurrentStatus,
    getMillLocalserverStatusReports,
    getMillLocalserverStatusSummary,
    LocalserverStatusReportsResponse,
    LocalServerStatusResponse,
    LocalserverStatusSummaryResponse,
} from "api/queries/MillsAPI";
import { useMemo, useState } from "react";
import { LocalserverStatusEnum } from "constants/constants";
import { IMillLocalserverEntity } from "./MillLocalserverEntityInterfaces";
import { ITableOperationFilters } from "entities/EventReports/EventReportsEntityInterfaces";
import { LocalserverStatusTableColumnsIdEnum } from "pages/Corporate/Locations/MillLocalserverMetrics/StatusReportsTableDetails/StatusReportsTableDetailsInterfaces";
import { processStatusReports } from "./helpers";

export const MillLocalserverEntity: IMillLocalserverEntity = {
    GetLocalserverCurrentStatus: (millId: number) => {
        const [isDisconnected, setIsDisconnected] = useState<boolean>(false);

        const { loading, error } = useSubscription<LocalServerStatusResponse>(getMillLocalserverCurrentStatus, {
            fetchPolicy: "no-cache",
            skip: !millId,
            variables: {
                where: {
                    end_date: { _is_null: true },
                    mill: { mill_id: { _eq: millId }, dedicated_localserver: { _eq: true } },
                },
            },
            onData: (data) => {
                const reports = data.data.data?.local_server_status_reports;
                if (!reports) {
                    setIsDisconnected(false);
                    return;
                }

                if (reports.length > 0) {
                    const isStatusDisconnected = reports[0].status_id === LocalserverStatusEnum.DISCONNECTED;
                    setIsDisconnected(isStatusDisconnected);
                } else {
                    setIsDisconnected(false);
                }
            },
        });

        return { loading, isDisconnected, error };
    },
    GetLocalserverStatusDetails: (
        millId: number,
        rangeHours: number,
        tableOperationFilters: ITableOperationFilters,
    ) => {
        const [statusAverages, setStatusAverages] = useState<
            LocalserverStatusSummaryResponse["get_local_server_status_summary"][0] | null
        >(null);
        const [statusReports, setStatusReports] = useState<
            LocalserverStatusReportsResponse["local_server_status_reports"]
        >([]);

        const orderBy = useMemo(
            () => ({
                ...(!Boolean(tableOperationFilters.orderBy) && { start_date: "desc" }),
                ...(tableOperationFilters.orderBy === LocalserverStatusTableColumnsIdEnum.TIME_STOP && {
                    start_date: tableOperationFilters.order,
                }),
                ...(tableOperationFilters.orderBy === LocalserverStatusTableColumnsIdEnum.TIME_RESTART && {
                    end_date: tableOperationFilters.order,
                }),
            }),
            [tableOperationFilters],
        );

        const {
            refetch: refetchAverages,
            error: errorAverages,
            loading: loadingAverages,
        } = useQuery<LocalserverStatusSummaryResponse>(getMillLocalserverStatusSummary, {
            fetchPolicy: "no-cache",
            variables: {
                args: {
                    p_mill_id: millId,
                    range_hours: rangeHours,
                },
            },
            onCompleted: (data) => {
                setStatusReports([]);
                if (data.get_local_server_status_summary.length > 0) {
                    setStatusAverages(data.get_local_server_status_summary[0]);
                } else {
                    setStatusAverages(null);
                }
            },
        });

        const {
            loading: loadingReports,
            error: errorReports,
            refetch: refetchReports,
        } = useQuery<LocalserverStatusReportsResponse>(getMillLocalserverStatusReports, {
            fetchPolicy: "no-cache",
            skip: !statusAverages,
            variables: {
                limit: tableOperationFilters.limit,
                offset: tableOperationFilters.offset,
                order_by: orderBy,
                where: {
                    _and: [
                        { status_id: { _eq: LocalserverStatusEnum.DISCONNECTED } },
                        {
                            _or: [
                                {
                                    _and: [
                                        { end_date: { _is_null: false } },
                                        { start_date: { _lte: statusAverages?.range_end_date } },
                                        { end_date: { _gte: statusAverages?.range_start_date } },
                                    ],
                                },
                                {
                                    _and: [
                                        { end_date: { _is_null: true } },
                                        { start_date: { _lte: statusAverages?.range_end_date } },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            },
            onCompleted: (data) => {
                const processedReports = processStatusReports(
                    data.local_server_status_reports,
                    statusAverages?.range_start_date!,
                    statusAverages?.range_end_date!,
                );
                setStatusReports(processedReports);
            },
        });

        const refetch = async () => {
            await refetchAverages();
            await refetchReports();
        };

        return {
            loading: loadingReports || loadingAverages,
            statusAverages,
            statusReports,
            apolloQueryError: errorReports || errorAverages,
            refetch,
        };
    },
};
